import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { Stage, Step } from 'webapp/services/account-progress';

interface Args {
	model: Step;
	stage: Stage | null;
	origin: string;
	onContentChange: () => void;
}

export default class AccountProgressStepComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('account-progress') declare accountProgressService: Service.AccountProgress;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked _isExpanded = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// set to cached variable to not trigger reposition (in isExpanded setter) on init
		this._isExpanded = !!this.isFirstIncomplete;
	}

	get stageId() {
		return this.args.stage?.id;
	}

	get isStageActive() {
		return (
			this.accountProgressService.activeStage &&
			this.accountProgressService.activeStage.id === this.stageId
		);
	}

	get isStageCompleted() {
		const stageId = this.stageId;
		return stageId ? this.accountProgressService.stage > stageId : false;
	}

	get isStageNotYetStarted() {
		return !this.isStageCompleted && !this.isStageActive;
	}

	get showStatus() {
		return !this.isStageNotYetStarted;
	}

	get isCompleted() {
		return (
			this.showStatus &&
			(this.isStageCompleted ||
				(this.accountProgressService.stepsCompleted || []).includes(this.args.model.id))
		);
	}

	get isFirstIncomplete() {
		const completedSteps = this.accountProgressService.stepsCompleted || [],
			currentStepIdx = this.args.stage?.steps.indexOf(this.args.model);
		return (
			typeof currentStepIdx !== 'undefined' &&
			!!this.isStageActive &&
			!this.isCompleted &&
			!this.args.stage?.steps.some((step, idx) => {
				return idx < currentStepIdx && !completedSteps.includes(step.id);
			})
		);
	}

	get primaryAction() {
		return this.args.model.actions[0];
	}

	get secondaryAction() {
		return this.args.model.actions[1];
	}

	get isPartnerStep() {
		return this.primaryAction?.id === 'partner';
	}

	get showPrimaryAction() {
		return this.primaryAction && !(this.isPartnerStep && !this.isStageActive);
	}

	get showSecondaryAction() {
		return !!this.secondaryAction;
	}

	get isExpanded() {
		return !!this._isExpanded;
	}

	set isExpanded(value) {
		if (this._isExpanded !== value) {
			this._isExpanded = value;
			later(this, this.args.onContentChange, 600); // because of the animation
		}
	}

	@action
	handleTitleClick(e: PointerEvent) {
		e.preventDefault();

		if (this.isExpanded) {
			if (this.isFirstIncomplete && this.showPrimaryAction) {
				this.doPrimaryAction(e);
			} else {
				this.isExpanded = false;
			}
		} else {
			this.isExpanded = true;
		}
	}

	@action
	doPrimaryAction(e: PointerEvent) {
		e.preventDefault();
		this.accountProgressService.doAction(
			this.primaryAction!,
			this.args.model,
			this.args.stage,
			this.args.origin,
		);
	}

	@action
	doSecondaryAction(e: PointerEvent) {
		e.preventDefault();
		this.accountProgressService.doAction(
			this.secondaryAction!,
			this.args.model,
			this.args.stage,
			this.args.origin,
		);
	}
	<template>
		<li
			class="{{if this.isCompleted 'done'}}
				{{if this.showStatus 'showStatus'}}
				{{if this.isExpanded 'expanded' 'collapsed'}}"
		>

			<div class="step-ct">
				<a role="button" class="title" {{on "click" this.handleTitleClick}}>
					<CommonUiIconComponent @name="Check" @size="xs" @class="step-check" />
					{{@model.name}}
				</a>

				<div class="more">
					{{#if @model.description}}
						<p>{{@model.description}}</p>
					{{/if}}
					{{#if this.showPrimaryAction}}
						<a
							role="button"
							class="button button-primary size-small"
							{{on "click" this.doPrimaryAction}}
						>
							{{this.primaryAction.name}}
						</a>
					{{/if}}
					{{#if this.showSecondaryAction}}
						<a
							role="button"
							class="button button-secondary theme-grey-outlined size-small"
							{{on "click" this.doSecondaryAction}}
						>
							{{this.secondaryAction.name}}
						</a>
					{{/if}}
				</div>
			</div>

		</li>
	</template>
}
